import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Clock, User, CheckCircle, XCircle, Shield, AlertTriangle,
  Zap, FileText, ExternalLink, MoreHorizontal, MessageSquare, History,
  Loader2, ChevronRight, Plus, AlertCircle, Lock
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Checkbox } from '../components/ui/checkbox';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator
} from '../components/ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle
} from '../components/ui/dialog';
import { Separator } from '../components/ui/separator';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import { getApproval, approveAndExecute, rejectApproval, subscribe, getTicket } from '../data/demoStore';

const mockApproval = {
  id: 'APR-002',
  type: 'elevated_access',
  title: 'AWS Admin Access for Alex Kim',
  description: 'Temporary admin access for production debugging. Required for incident INC-4521.',
  justification: 'There is an ongoing production incident affecting user authentication. Need admin access to review CloudWatch logs and update IAM policies if necessary.',
  requester: {
    name: 'Alex Kim',
    email: 'alex.kim@company.com',
    avatar: 'AK',
    department: 'Engineering',
    manager: 'Jordan Lee',
    title: 'Senior Software Engineer'
  },
  ticket: { id: 'TKT-1240', subject: 'Need AWS admin for incident' },
  workflow: 'Elevated Access Request',
  actions: [
    { id: 'act-1', system: 'AWS IAM', action: 'Grant AdministratorAccess policy', reversible: true },
    { id: 'act-2', system: 'Okta', action: 'Add to AWS-Admins group (24h TTL)', reversible: true },
    { id: 'act-3', system: 'Audit Log', action: 'Enable enhanced session logging', reversible: false }
  ],
  risk: 'high',
  riskFactors: [
    'Grants broad AWS permissions',
    'Access to production environment',
    'Can modify IAM policies'
  ],
  mitigations: [
    '24-hour time-limited access',
    'Enhanced audit logging enabled',
    'Session recording active',
    'Automatic revocation on expiry'
  ],
  requestedAt: '2024-01-15T09:15:00Z',
  deadline: '2024-01-15T13:15:00Z',
  expiresAt: '2024-01-16T09:15:00Z',
  status: 'pending',
  approvers: [
    { name: 'Jordan Lee', role: 'Manager', status: 'approved', decidedAt: '2024-01-15T09:30:00Z', comment: 'Approved for incident response' },
    { name: 'Security Team', role: 'Security', status: 'pending', decidedAt: null, comment: null }
  ],
  auditLog: [
    { action: 'Request created', actor: 'Alex Kim', timestamp: '2024-01-15T09:15:00Z' },
    { action: 'Routed to Manager approval', actor: 'System', timestamp: '2024-01-15T09:15:05Z' },
    { action: 'Approved by Manager', actor: 'Jordan Lee', timestamp: '2024-01-15T09:30:00Z' },
    { action: 'Escalated to Security Team', actor: 'System', timestamp: '2024-01-15T09:30:10Z' }
  ],
  similarRequests: [
    { id: 'APR-H010', title: 'AWS Admin Access for Taylor Brown', status: 'approved', date: '2024-01-10' },
    { id: 'APR-H008', title: 'AWS Admin Access for Jordan Lee', status: 'approved', date: '2024-01-05' }
  ]
};

const riskConfig = {
  low: { label: 'Low Risk', color: 'bg-green-100 text-green-800 border-green-200' },
  medium: { label: 'Medium Risk', color: 'bg-amber-100 text-amber-800 border-amber-200' },
  high: { label: 'High Risk', color: 'bg-red-100 text-red-800 border-red-200' }
};

const typeConfig = {
  access_request: { label: 'Access Request', icon: User },
  elevated_access: { label: 'Elevated Access', icon: Shield },
  hardware_request: { label: 'Hardware', icon: FileText },
  workflow_publish: { label: 'Workflow Publish', icon: Zap }
};

export const ApprovalDetail = () => {
  const { approvalId } = useParams();
  const navigate = useNavigate();
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [showConstraintDialog, setShowConstraintDialog] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [approveComment, setApproveComment] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [constraints, setConstraints] = useState({
    reduceDuration: false,
    duration: '24',
    requireMFA: false,
    notifyOnAccess: true,
    additionalApprover: false,
    additionalApproverEmail: ''
  });

  // Load from demoStore first, fall back to mock
  const [demoApproval, setDemoApproval] = useState(() => getApproval(approvalId));

  useEffect(() => {
    return subscribe(() => {
      setDemoApproval(getApproval(approvalId));
    });
  }, [approvalId]);

  // Build approval object from demoStore or use mock
  const approval = demoApproval ? {
    ...demoApproval,
    type: 'access_request',
    requester: demoApproval.requester || { name: 'Demo User', avatar: 'DU', department: 'Sales' },
    ticket: demoApproval.ticketId ? { id: demoApproval.ticketId, subject: demoApproval.description } : null,
    workflow: 'Demo Workflow',
    riskFactors: ['Grants access to business application'],
    mitigations: ['Requires manager approval', 'Audit logging enabled'],
    deadline: new Date(Date.now() + 8 * 60 * 60 * 1000).toISOString(),
    approvers: [{ name: 'Mike Torres', role: 'Manager', status: demoApproval.status === 'pending' ? 'pending' : 'approved' }],
    auditLog: [
      { action: 'Request created', actor: demoApproval.requester?.name || 'User', timestamp: demoApproval.requestedAt },
    ],
  } : mockApproval;

  const risk = riskConfig[approval.risk] || riskConfig.low;
  const type = typeConfig[approval.type] || typeConfig.access_request;
  const TypeIcon = type.icon;

  const getTimeUntilDeadline = (deadline) => {
    const now = new Date();
    const dl = new Date(deadline);
    const diff = dl - now;
    if (diff < 0) return 'Overdue';
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    if (hours > 24) return `${Math.floor(hours / 24)}d ${hours % 24}h remaining`;
    if (hours > 0) return `${hours}h ${mins}m remaining`;
    return `${mins}m remaining`;
  };

  const isUrgent = (new Date(approval.deadline) - new Date()) < 4 * 60 * 60 * 1000;
  const isPending = approval.approvers.some(a => a.status === 'pending' && a.name === 'Security Team');

  const handleApprove = async () => {
    setIsProcessing(true);
    await new Promise(resolve => setTimeout(resolve, 1500));
    // Use demoStore function if it's a demo approval
    if (demoApproval) {
      approveAndExecute(approvalId);
    }
    setIsProcessing(false);
    setShowApproveDialog(false);
    toast.success('Approved and executed!', {
      description: 'Actions have been executed successfully.'
    });
    navigate('/approvals');
  };

  const handleApproveWithConstraints = async () => {
    setIsProcessing(true);
    await new Promise(resolve => setTimeout(resolve, 1500));
    // Use demoStore function if it's a demo approval
    if (demoApproval) {
      approveAndExecute(approvalId);
    }
    setIsProcessing(false);
    setShowConstraintDialog(false);
    const appliedConstraints = [];
    if (constraints.reduceDuration) appliedConstraints.push(`Duration reduced to ${constraints.duration}h`);
    if (constraints.requireMFA) appliedConstraints.push('MFA required');
    if (constraints.notifyOnAccess) appliedConstraints.push('Notifications enabled');
    toast.success('Approved with constraints', {
      description: appliedConstraints.join(', ') || 'Actions executed'
    });
    navigate('/approvals');
  };

  const handleReject = async () => {
    setIsProcessing(true);
    await new Promise(resolve => setTimeout(resolve, 1500));
    // Use demoStore function if it's a demo approval
    if (demoApproval) {
      rejectApproval(approvalId, rejectReason || 'Request denied');
    }
    setIsProcessing(false);
    setShowRejectDialog(false);
    toast.success('Request rejected', {
      description: 'Requester has been notified.'
    });
    navigate('/approvals');
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        navigate('/approvals');
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [navigate]);

  return (
    <div className="min-h-full bg-background">
      <div className="border-b bg-card">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <div className="flex items-center gap-4">
            <Link to="/approvals" aria-label="Back to approvals">
              <Button variant="ghost" size="icon">
                <ArrowLeft className="w-4 h-4" aria-hidden="true" />
              </Button>
            </Link>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="text-sm font-mono text-muted-foreground">{approval.id}</span>
                <Badge variant="outline" className="text-xs">
                  <TypeIcon className="w-3 h-3 mr-1" aria-hidden="true" />
                  {type.label}
                </Badge>
                <Badge variant="outline" className={cn("text-xs", risk.color)}>
                  {risk.label}
                </Badge>
                {isUrgent && (
                  <Badge variant="destructive" className="text-xs">Urgent</Badge>
                )}
              </div>
              <h1 className="text-lg font-semibold text-foreground">{approval.title}</h1>
            </div>
            <div className="flex items-center gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="icon" aria-label="More actions">
                    <MoreHorizontal className="w-4 h-4" aria-hidden="true" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem>
                    <History className="w-4 h-4 mr-2" aria-hidden="true" />
                    View Full History
                  </DropdownMenuItem>
                  <DropdownMenuItem>
                    <MessageSquare className="w-4 h-4 mr-2" aria-hidden="true" />
                    Contact Requester
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem>
                    <ExternalLink className="w-4 h-4 mr-2" aria-hidden="true" />
                    Open Related Ticket
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-6">
        <div className="grid grid-cols-12 gap-6">
          <div className="col-span-8 space-y-6">
            {approval.risk === 'high' && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  <strong>High Risk Request:</strong> This request grants elevated privileges.
                  Review carefully before approving.
                </AlertDescription>
              </Alert>
            )}

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Request Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground mb-1">Description</h4>
                  <p className="text-sm text-foreground">{approval.description}</p>
                </div>
                <Separator />
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground mb-1">Business Justification</h4>
                  <p className="text-sm text-foreground">{approval.justification}</p>
                </div>
                {approval.ticket && (
                  <>
                    <Separator />
                    <div>
                      <h4 className="text-sm font-medium text-muted-foreground mb-1">Related Ticket</h4>
                      <Link
                        to={`/tickets/${approval.ticket.id}`}
                        className="inline-flex items-center gap-2 text-sm text-primary hover:underline"
                      >
                        <ExternalLink className="w-4 h-4" aria-hidden="true" />
                        {approval.ticket.id}: {approval.ticket.subject}
                      </Link>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Actions to Execute</CardTitle>
                <CardDescription>These actions will be performed upon approval</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {approval.actions.map((action, idx) => (
                    <div
                      key={action.id}
                      className="flex items-start gap-3 p-3 rounded-lg bg-muted/50"
                      role="listitem"
                    >
                      <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-xs font-medium text-primary shrink-0">
                        {idx + 1}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Badge variant="secondary" className="text-xs">{action.system}</Badge>
                          {action.reversible && (
                            <Badge variant="outline" className="text-xs text-green-700 bg-green-50">Reversible</Badge>
                          )}
                        </div>
                        <p className="text-sm text-foreground mt-1">{action.action}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {approval.risk === 'high' && (
              <Card className="border-red-200">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2 text-red-800">
                    <AlertTriangle className="w-4 h-4" aria-hidden="true" />
                    Risk Assessment
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <h4 className="text-sm font-medium text-red-800 mb-2">Risk Factors</h4>
                      <ul className="space-y-2">
                        {approval.riskFactors.map((factor, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm text-red-700">
                            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
                            {factor}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-green-800 mb-2">Mitigations</h4>
                      <ul className="space-y-2">
                        {approval.mitigations.map((mitigation, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm text-green-700">
                            <CheckCircle className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
                            {mitigation}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Approval Chain</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {approval.approvers.map((approver, idx) => (
                    <div
                      key={idx}
                      className={cn(
                        "flex items-start gap-4 p-4 rounded-lg border",
                        approver.status === 'approved' && "bg-green-50 border-green-200",
                        approver.status === 'pending' && "bg-amber-50 border-amber-200",
                        approver.status === 'rejected' && "bg-red-50 border-red-200"
                      )}
                    >
                      <div className={cn(
                        "w-8 h-8 rounded-full flex items-center justify-center shrink-0",
                        approver.status === 'approved' && "bg-green-100",
                        approver.status === 'pending' && "bg-amber-100",
                        approver.status === 'rejected' && "bg-red-100"
                      )}>
                        {approver.status === 'approved' && <CheckCircle className="w-4 h-4 text-green-600" />}
                        {approver.status === 'pending' && <Clock className="w-4 h-4 text-amber-600" />}
                        {approver.status === 'rejected' && <XCircle className="w-4 h-4 text-red-600" />}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-foreground">{approver.name}</span>
                          <Badge variant="outline" className="text-xs">{approver.role}</Badge>
                        </div>
                        {approver.decidedAt && (
                          <p className="text-xs text-muted-foreground mt-1">
                            {approver.status === 'approved' ? 'Approved' : 'Rejected'} on {new Date(approver.decidedAt).toLocaleString()}
                          </p>
                        )}
                        {approver.comment && (
                          <p className="text-sm text-muted-foreground mt-2 italic">"{approver.comment}"</p>
                        )}
                        {approver.status === 'pending' && (
                          <p className="text-xs text-amber-600 mt-1">Awaiting decision</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="col-span-4 space-y-6">
            {isPending && (
              <Card className="border-primary">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Your Decision</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Button className="w-full" onClick={() => setShowApproveDialog(true)}>
                    <CheckCircle className="w-4 h-4 mr-2" aria-hidden="true" />
                    Approve
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => setShowConstraintDialog(true)}
                  >
                    <Lock className="w-4 h-4 mr-2" aria-hidden="true" />
                    Approve with Constraints
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full text-red-600 hover:text-red-700 hover:bg-red-50"
                    onClick={() => setShowRejectDialog(true)}
                  >
                    <XCircle className="w-4 h-4 mr-2" aria-hidden="true" />
                    Reject
                  </Button>
                </CardContent>
              </Card>
            )}

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Requester</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center text-sm font-medium">
                    {approval.requester.avatar}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">{approval.requester.name}</p>
                    <p className="text-xs text-muted-foreground">{approval.requester.title}</p>
                  </div>
                </div>
                <Separator />
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Department</span>
                    <span className="text-foreground">{approval.requester.department}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Manager</span>
                    <span className="text-foreground">{approval.requester.manager}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Email</span>
                    <span className="text-foreground">{approval.requester.email}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Timeline</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Requested</span>
                  <span className="text-foreground">{new Date(approval.requestedAt).toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Deadline</span>
                  <span className={cn("font-medium", isUrgent ? "text-red-600" : "text-foreground")}>
                    {getTimeUntilDeadline(approval.deadline)}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Access Expires</span>
                  <span className="text-foreground">{new Date(approval.expiresAt).toLocaleString()}</span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Similar Past Requests</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {approval.similarRequests.map((req) => (
                    <div
                      key={req.id}
                      className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/50 transition-colors"
                    >
                      <div>
                        <p className="text-xs font-mono text-muted-foreground">{req.id}</p>
                        <p className="text-sm text-foreground truncate max-w-[180px]">{req.title}</p>
                      </div>
                      <Badge
                        variant="outline"
                        className={cn(
                          "text-xs",
                          req.status === 'approved' && "bg-green-100 text-green-800"
                        )}
                      >
                        {req.status}
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Audit Log</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {approval.auditLog.map((entry, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <div className="w-2 h-2 rounded-full bg-muted-foreground mt-1.5 shrink-0" />
                      <div>
                        <p className="text-sm text-foreground">{entry.action}</p>
                        <p className="text-xs text-muted-foreground">
                          {entry.actor} &middot; {new Date(entry.timestamp).toLocaleTimeString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <Dialog open={showApproveDialog} onOpenChange={setShowApproveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-500" />
              Confirm Approval
            </DialogTitle>
            <DialogDescription>
              You're about to approve: <strong>{approval.title}</strong>
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <Label htmlFor="approve-comment">Comment (optional)</Label>
              <Textarea
                id="approve-comment"
                placeholder="Add a comment for the audit log..."
                value={approveComment}
                onChange={(e) => setApproveComment(e.target.value)}
                rows={2}
                className="mt-1.5"
              />
            </div>
            {approval.risk === 'high' && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  This is a high-risk request. Ensure you have verified the business justification.
                </AlertDescription>
              </Alert>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowApproveDialog(false)} disabled={isProcessing}>
              Cancel
            </Button>
            <Button onClick={handleApprove} disabled={isProcessing}>
              {isProcessing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" aria-hidden="true" />
                  Processing...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" aria-hidden="true" />
                  Confirm Approval
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showConstraintDialog} onOpenChange={setShowConstraintDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5 text-primary" />
              Approve with Constraints
            </DialogTitle>
            <DialogDescription>
              Apply additional restrictions to this approval.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="flex items-start gap-3">
              <Checkbox
                id="reduce-duration"
                checked={constraints.reduceDuration}
                onCheckedChange={(checked) => setConstraints(prev => ({ ...prev, reduceDuration: checked }))}
              />
              <div className="flex-1">
                <Label htmlFor="reduce-duration" className="font-medium">Reduce access duration</Label>
                <p className="text-xs text-muted-foreground">Limit access to a shorter period</p>
                {constraints.reduceDuration && (
                  <div className="mt-2 flex items-center gap-2">
                    <Input
                      type="number"
                      value={constraints.duration}
                      onChange={(e) => setConstraints(prev => ({ ...prev, duration: e.target.value }))}
                      className="w-20"
                      min="1"
                      max="24"
                    />
                    <span className="text-sm text-muted-foreground">hours</span>
                  </div>
                )}
              </div>
            </div>

            <Separator />

            <div className="flex items-start gap-3">
              <Checkbox
                id="require-mfa"
                checked={constraints.requireMFA}
                onCheckedChange={(checked) => setConstraints(prev => ({ ...prev, requireMFA: checked }))}
              />
              <div>
                <Label htmlFor="require-mfa" className="font-medium">Require MFA for each access</Label>
                <p className="text-xs text-muted-foreground">User must authenticate with MFA each time</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Checkbox
                id="notify-access"
                checked={constraints.notifyOnAccess}
                onCheckedChange={(checked) => setConstraints(prev => ({ ...prev, notifyOnAccess: checked }))}
              />
              <div>
                <Label htmlFor="notify-access" className="font-medium">Notify on access</Label>
                <p className="text-xs text-muted-foreground">Send notification each time access is used</p>
              </div>
            </div>

            <Separator />

            <div className="flex items-start gap-3">
              <Checkbox
                id="additional-approver"
                checked={constraints.additionalApprover}
                onCheckedChange={(checked) => setConstraints(prev => ({ ...prev, additionalApprover: checked }))}
              />
              <div className="flex-1">
                <Label htmlFor="additional-approver" className="font-medium">Require additional approval</Label>
                <p className="text-xs text-muted-foreground">Add another approver to the chain</p>
                {constraints.additionalApprover && (
                  <div className="mt-2">
                    <Input
                      type="email"
                      placeholder="approver@company.com"
                      value={constraints.additionalApproverEmail}
                      onChange={(e) => setConstraints(prev => ({ ...prev, additionalApproverEmail: e.target.value }))}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConstraintDialog(false)} disabled={isProcessing}>
              Cancel
            </Button>
            <Button onClick={handleApproveWithConstraints} disabled={isProcessing}>
              {isProcessing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" aria-hidden="true" />
                  Processing...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" aria-hidden="true" />
                  Approve with Constraints
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <XCircle className="w-5 h-5 text-red-500" />
              Reject Request
            </DialogTitle>
            <DialogDescription>
              Provide a reason for rejecting this request.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="reject-reason">Rejection reason</Label>
            <Textarea
              id="reject-reason"
              placeholder="Enter rejection reason..."
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={3}
              className="mt-1.5"
            />
            <p className="text-xs text-muted-foreground mt-2">
              This reason will be shared with the requester and recorded in the audit log.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRejectDialog(false)} disabled={isProcessing}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleReject}
              disabled={!rejectReason.trim() || isProcessing}
            >
              {isProcessing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" aria-hidden="true" />
                  Processing...
                </>
              ) : (
                <>
                  <XCircle className="w-4 h-4 mr-2" aria-hidden="true" />
                  Reject Request
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ApprovalDetail;
